import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";

import api from "../Utils/api";
import config from "../Utils/config";
import session from "../Utils/session";
import logo from "../Assets/logo.png";
import Button from "../Components/Button";
import { usePlausible } from "../hooks/usePlausible";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { trackEvent } = usePlausible();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [oidcLogin, setOidcLogin] = useState(false);

  useEffect(() => {
    if (session.isAuthenticated()) {
      navigate("/app");
    }
  }, [navigate]);

  useEffect(() => {
    api.getPublicSettings()
      .then((settings) => setOidcLogin(!!settings.oidcLogin))
      .catch(() => setOidcLogin(false));
  }, []);

  useEffect(() => {
    // Check for success message from password reset
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear the message from location state
      navigate(location.pathname, { replace: true, state: {} });
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
    }
  }, [location, navigate]);

  const onSubmit = (event) => {
    event.preventDefault();
    setIsLoading(true);
    setError(null);

    api
      .signIn({ username, password })
      .then((resp) => {
        trackEvent('Login', { username });
        session.setSession(resp.access_token);
        navigate("/app");
      })
      .catch((err) => {
        setIsLoading(false);
        if (err.message) {
          setError(err.message);
        } else {
          setError("Could not communicate with the server properly.");
        }
      });
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen w-full bg-primary p-4">
      <div className="w-full max-w-md flex flex-col items-center">
        {/* Back to Home Button */}
        <Link
          to="/"
          className="self-start mb-4 flex items-center text-gray-400 hover:text-white transition-colors group"
        >
          <svg className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Home
        </Link>

        {/* Card Container */}
        <div className="w-full bg-secondary border border-neutral-800 rounded-md shadow-2xl p-8 md:p-10 animate-scale-in">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img
              src={logo}
              alt="Muzio Logo"
              className="w-24 h-24 md:w-32 md:h-32 hover:scale-105 transition-transform"
            />
          </div>

          {/* Title */}
          <h1 className="text-2xl md:text-3xl font-bold text-white text-center mb-2">
            Welcome Back
          </h1>
          <p className="text-gray-400 text-center mb-8">
            Sign in to continue to Muzio
          </p>

          {/* Form */}
          <form onSubmit={onSubmit} className="space-y-5">
            {/* Success Message */}
            {successMessage && (
              <div className="bg-green-900 bg-opacity-30 border border-green-600 text-green-300 px-4 py-3 rounded-lg animate-slide-down">
                <p className="text-sm text-center">{successMessage}</p>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-accent bg-opacity-20 border border-accent text-white px-4 py-3 rounded-lg animate-slide-down">
                <p className="text-sm text-center">{error}</p>
              </div>
            )}

            {/* Username Input */}
            <div className="space-y-2">
              <label htmlFor="username" className="block text-sm font-medium text-gray-300">
                Username
              </label>
              <input
                id="username"
                type="text"
                name="username"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoFocus
                className="w-full px-3 py-2.5 bg-neutral-800 border border-neutral-700 rounded-md text-white placeholder-text-dim focus:outline-none focus:border-accent transition-colors"
              />
            </div>

            {/* Password Input */}
            <div className="space-y-2">
              <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-3 py-2.5 bg-neutral-800 border border-neutral-700 rounded-md text-white placeholder-text-dim focus:outline-none focus:border-accent transition-colors pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isLoading}
              variant="primary"
              size="lg"
              className="w-full"
            >
              {isLoading ? "Signing in..." : "Sign In"}
            </Button>
          </form>

          {/* Sign in with ioprodz */}
          {oidcLogin && (
            <>
              <div className="flex items-center my-6">
                <div className="flex-1 border-t border-neutral-700" />
                <span className="px-3 text-sm text-gray-500">or</span>
                <div className="flex-1 border-t border-neutral-700" />
              </div>
              <a
                href={`${config.serverUrl}/users/oidc/login`}
                className="block w-full text-center px-4 py-3 rounded-md border border-neutral-600 text-white font-medium hover:border-accent transition-colors"
              >
                Sign in with ioprodz
              </a>
            </>
          )}

          {/* Sign Up Link */}
          <p className="text-center text-gray-400 mt-6">
            Don't have an account?{" "}
            <Link
              to="/join"
              className="text-accent hover:text-accent font-medium transition-colors"
            >
              Create one here
            </Link>
          </p>

          {/* Forgot Password Link */}
          <p className="text-center text-gray-400 mt-3">
            <Link
              to="/forgot-password"
              className="text-accent hover:text-accent font-medium transition-colors"
            >
              Forgot your password?
            </Link>
          </p>
        </div>

        {/* Footer */}
        <p className="text-center text-gray-500 text-sm mt-6">
          © 2024 Muzio. All rights reserved.
        </p>
      </div>
    </div>
  );
}
